import {
  DeleteExchangesSettingsOverridesV2KindsEnum,
  PbExchangeOrdersSettingsV2,
  PbExchangePolicyV2,
  PbExchangesSettingsV2,
  PbFullCatalogSettingsV2,
  PbGetExchangesSettingsV2Response,
  PbInstantExchangeSettingsV2
} from '@itsrever/rever-api'
import {
  DEFAULT_ORDERS,
  ExchangeFlow,
  ExchangeOrders,
  ExchangePolicy,
  ExchangesKind,
  ExchangesSettings,
  FullCatalog,
  GroupExchanges,
  InstantExchange,
  InstantExchangeTiming,
  Layered
} from '@/domain/exchanges'

export const PB_KINDS: Record<
  ExchangesKind,
  DeleteExchangesSettingsOverridesV2KindsEnum
> = {
  policy: DeleteExchangesSettingsOverridesV2KindsEnum.Policy,
  orders: DeleteExchangesSettingsOverridesV2KindsEnum.ExchangeOrders,
  catalog: DeleteExchangesSettingsOverridesV2KindsEnum.FullCatalog,
  instant: DeleteExchangesSettingsOverridesV2KindsEnum.InstantExchange
}

export function toGroupExchanges(
  response: PbGetExchangesSettingsV2Response
): GroupExchanges {
  const settings = response.settings
  const policy: Layered<ExchangePolicy> = {
    group: settings?.policy && toPolicy(settings.policy),
    overrides: {}
  }
  const orders: Layered<ExchangeOrders> = {
    group: settings?.exchange_orders && toOrders(settings.exchange_orders),
    overrides: {}
  }
  const catalog: Layered<FullCatalog> = {
    group: settings?.full_catalog && toFullCatalog(settings.full_catalog),
    overrides: {}
  }
  const instant: Layered<InstantExchange> = {
    group: settings?.instant_exchange && toInstant(settings.instant_exchange),
    overrides: {}
  }
  for (const override of response.overrides ?? []) {
    const id = override.ecommerce_id ?? ''
    if (override.settings?.policy)
      policy.overrides[id] = toPolicy(override.settings.policy)
    if (override.settings?.exchange_orders)
      orders.overrides[id] = toOrders(override.settings.exchange_orders)
    if (override.settings?.full_catalog)
      catalog.overrides[id] = toFullCatalog(override.settings.full_catalog)
    if (override.settings?.instant_exchange)
      instant.overrides[id] = toInstant(override.settings.instant_exchange)
  }
  return {
    policy,
    orders,
    catalog,
    instant,
    multipleStores: response.multiple_stores ?? false
  }
}

const toPolicy = (policy: PbExchangePolicyV2): ExchangePolicy => ({
  exchangeFlow: (policy.exchange_flow ?? 'NO_EXCHANGE_FLOW') as ExchangeFlow,
  threshold: policy.threshold ?? 0,
  destinationScopedStock: policy.destination_scoped_stock ?? false
})

const toOrders = (orders: PbExchangeOrdersSettingsV2): ExchangeOrders => ({
  price: orders.price === 'ZERO' ? 'ZERO' : 'FULL',
  shippingLine: (orders.shipping_line ||
    DEFAULT_ORDERS.shippingLine) as ExchangeOrders['shippingLine'],
  paymentMethod:
    orders.payment_method === 'SAME_AS_ORIGINAL' ? 'SAME_AS_ORIGINAL' : '',
  carryOverDiscounts:
    orders.carry_over_automatic_discounts_on_exchanges ?? false
})

const toFullCatalog = (catalog: PbFullCatalogSettingsV2): FullCatalog => ({
  menuId: catalog.platform_main_menu_id ?? '',
  excludedProductIds: catalog.excluded_product_ids ?? [],
  excludedCollectionIds: catalog.excluded_collection_ids ?? [],
  excludedTags: catalog.excluded_tags ?? [],
  onlineStoreOnly: catalog.online_store_only ?? false
})

const toInstant = (instant: PbInstantExchangeSettingsV2): InstantExchange => ({
  enabled: instant.enabled ?? false,
  feeType: instant.fee_type ?? 'INSTANT_EXCHANGE_FEE_TYPE_UNSPECIFIED',
  percentageFeeValue: instant.percentage_fee_value ?? 0,
  fixedFeeAmount: instant.fixed_fee_amount ?? 0,
  feeCapAmount: instant.fee_cap_amount ?? 0,
  feeFloorAmount: instant.fee_floor_amount ?? 0,
  timing: (instant.timing ?? 'NO_TIMING') as InstantExchangeTiming
})

export function toPbSettings(
  kind: ExchangesKind,
  value: ExchangesSettings
): PbExchangesSettingsV2 {
  switch (kind) {
    case 'policy': {
      const policy = value as ExchangePolicy
      return {
        policy: {
          exchange_flow: policy.exchangeFlow,
          threshold: policy.threshold,
          destination_scoped_stock: policy.destinationScopedStock
        }
      }
    }
    case 'orders': {
      const orders = value as ExchangeOrders
      return {
        exchange_orders: {
          price: orders.price,
          shipping_line: orders.shippingLine,
          payment_method: orders.paymentMethod,
          carry_over_automatic_discounts_on_exchanges: orders.carryOverDiscounts
        }
      }
    }
    case 'catalog': {
      const catalog = value as FullCatalog
      return {
        full_catalog: {
          platform_main_menu_id: catalog.menuId,
          excluded_product_ids: catalog.excludedProductIds,
          excluded_collection_ids: catalog.excludedCollectionIds,
          excluded_tags: catalog.excludedTags,
          online_store_only: catalog.onlineStoreOnly
        }
      }
    }
    case 'instant': {
      const instant = value as InstantExchange
      return {
        instant_exchange: {
          enabled: instant.enabled,
          fee_type: instant.feeType,
          percentage_fee_value: instant.percentageFeeValue,
          fixed_fee_amount: instant.fixedFeeAmount,
          fee_cap_amount: instant.feeCapAmount,
          fee_floor_amount: instant.feeFloorAmount,
          timing: instant.timing
        }
      }
    }
  }
}
